import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as apiClient from '../lib/api';
import { mediaUrl, coverForSlug } from '../lib/media';
import { useAuth } from '../components/auth';
import MaterialViewer from '../components/MaterialViewer';
import WorkbookWorkspace from '../components/WorkbookWorkspace';
import {
  BookOpen,
  Upload,
  Plus,
  Trash2,
  FileText,
  Users,
  Eye,
  EyeOff,
  Gift,
  TrendingUp,
  Search,
  Edit3,
  Download,
  Settings,
  ChevronRight,
  ChevronDown,
  Zap,
  Target,
  Activity,
  BookMarked,
  Layers,
  Star,
  MessageSquare,
  LogOut,
  Shield,
  Loader2,
  Send,
  MessageCircle,
  UserCheck,
  Radio,
  Share2,
  DollarSign,
  Copy,
  Check,
  Image,
  File,
  Save,
  CheckCircle,
  Flame,
  Highlighter,
  StickyNote,
  PlayCircle,
  Menu,
  X
} from 'lucide-react';

// Admin email - only this user can access admin functions
// Admin status comes from the API (user.isAdmin) — not hardcoded

export default function Dashboard() {
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();

  // REST data state
  const [_analytics, setAnalyticsData] = useState<any>(null);
  const [_books, setBooksData] = useState<any[] | null>(null);
  const [_materials, setMaterialsData] = useState<any[] | null>(null);
  const [members, setMembers] = useState<any[] | null>(null);
  const [customers, setCustomers] = useState<any[] | null>(null);
  const [customerStats, setCustomerStats] = useState<any>(null);
  const [broadcastHistory, setBroadcastHistory] = useState<any[] | null>(null);
  const [affiliates, setAffiliates] = useState<any[] | null>(null);
  const [affiliateStats, setAffiliateStats] = useState<any>(null);
  const [revenueMetrics, setRevenueMetrics] = useState<any>(null);
  const [engagementMetrics, setEngagementMetrics] = useState<any>(null);
  const [contentMetrics, setContentMetrics] = useState<any>(null);
  const [userReadingStats, setUserReadingStats] = useState<any>(null);
  const [libraryProgress, setLibraryProgress] = useState<any>(null);

  // Local state — declared BEFORE any derived values or useEffects to avoid TDZ in production builds
  const [selectedBook, setSelectedBook] = useState<string | null>(null);
  // Default tab is always books now (no more overview)
  const [activeTab, setActiveTab] = useState<'books' | 'materials' | 'members' | 'customers' | 'broadcast' | 'affiliates' | 'analytics' | 'agents'>('books');
  const [searchQuery, setSearchQuery] = useState('');

  // Agent management state (admin only)
  const [agentsList, setAgentsList] = useState<any[] | null>(null);
  const [agentLog, setAgentLog] = useState<any[] | null>(null);
  const [pendingInsights, setPendingInsights] = useState<any[] | null>(null);
  const [showAddBook, setShowAddBook] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [newBookTitle, setNewBookTitle] = useState('');
  const [newBookSubtitle, setNewBookSubtitle] = useState('');
  const [newBookSlug, setNewBookSlug] = useState('');
  const [newMaterialName, setNewMaterialName] = useState('');
  const [newMaterialDesc, setNewMaterialDesc] = useState('');
  const [newMaterialUrl, setNewMaterialUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // File upload state
  const [manuscriptFile, setManuscriptFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [materialFile, setMaterialFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [parsedChapters, setParsedChapters] = useState<Array<{ number: number; title: string; content: string }> | null>(null);
  const [selectedBookForMaterial, setSelectedBookForMaterial] = useState<string | null>(null);

  // Chapter editing state
  const [showAddChapter, setShowAddChapter] = useState(false);
  const [editingChapter, setEditingChapter] = useState<{ id: string; title: string; content: string; isFreePreview: boolean } | null>(null);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterContent, setNewChapterContent] = useState('');

  // View mode toggle (admin can switch to see customer view) - persisted to localStorage
  const [viewMode, setViewMode] = useState<'admin' | 'customer'>(() => {
    const saved = localStorage.getItem('dashboard-view-mode');
    return saved === 'customer' ? 'customer' : 'admin';
  });

  useEffect(() => {
    localStorage.setItem('dashboard-view-mode', viewMode);
  }, [viewMode]);

  // Material viewer state
  const [viewingMaterial, setViewingMaterial] = useState<{
    id: string;
    name: string;
    description?: string;
    type: 'pdf' | 'video' | 'guide' | 'template';
    fileUrl: string;
    fileSize?: string;
  } | null>(null);

  // Customer sidebar state - expanded books
  const [expandedBooks, setExpandedBooks] = useState<Set<string>>(new Set());
  const [expandedWorkbooks, setExpandedWorkbooks] = useState<Set<string>>(new Set());
  const [selectedChapter, setSelectedChapter] = useState<{ bookId: string; chapterId: string; chapterNumber: number } | null>(null);
  const [selectedWorkbook, setSelectedWorkbook] = useState<{
    id: string;
    name: string;
    description?: string;
    type: 'pdf' | 'video' | 'guide' | 'template';
    fileUrl: string;
    bookId?: string;
  } | null>(null);

  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // User menu dropdown state
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Derived values (must come AFTER all useState calls to avoid TDZ in production builds)
  const analytics = _analytics ?? { totalUsers: 0, totalBooks: 0, totalDownloads: 0, recentSignups: 0, activeReaders: 0, purchasedUsers: 0 };
  const books = _books ?? [];
  const materials = _materials ?? [];

  // Filter books by search query (title or slug)
  const filteredBooks = searchQuery.trim()
    ? books.filter((b: any) => {
        const q = searchQuery.trim().toLowerCase();
        return (b.title?.toLowerCase().includes(q) || b.slug?.toLowerCase().includes(q));
      })
    : books;

  // Fetch data available to all authenticated users
  useEffect(() => {
    if (!isAuthenticated) return;
    apiClient.dashboard.books()
      .catch(() => apiClient.books.list())
      .then(async (dashBooks: any[]) => {
        if (!dashBooks) return;
        // Enrich with full chapter data from /api/books/:slug
        const enriched = await Promise.all(dashBooks.map(async (b: any) => {
          try {
            const full = await apiClient.books.getBySlug(b.slug);
            const rawCover = full?.coverUrl || (full as any)?.cover_url || b.coverUrl || b.cover_url;
            return { ...b, chapters: full?.chapters ?? [], materials: full?.materials ?? [], coverUrl: rawCover ? mediaUrl(rawCover) : coverForSlug(b.slug) };
          } catch {
            const rawCover = b.coverUrl || b.cover_url;
            return { ...b, chapters: [], materials: [], coverUrl: rawCover ? mediaUrl(rawCover) : coverForSlug(b.slug) };
          }
        }));
        setBooksData(enriched);
      }).catch(() => {});
  }, [isAuthenticated]);

  // Fetch admin-only data when user is confirmed admin
  useEffect(() => {
    if (!user?.isAdmin) return;
    apiClient.dashboard.analytics().then(setAnalyticsData).catch(() => {});
    apiClient.dashboard.materials().then(setMaterialsData).catch(() => {});
    apiClient.dashboard.members().then(setMembers).catch(() => {});
    apiClient.admin.customers().then(setCustomers).catch(() => {});
    apiClient.admin.stats().then(setCustomerStats).catch(() => {});
    apiClient.admin.broadcastHistory(10).then(setBroadcastHistory).catch(() => {});
    apiClient.affiliates.list().then(setAffiliates).catch(() => {});
    apiClient.affiliates.stats().then(setAffiliateStats).catch(() => {});
    apiClient.analytics.revenue().then(setRevenueMetrics).catch(() => {});
    apiClient.analytics.engagement().then(setEngagementMetrics).catch(() => {});
    apiClient.analytics.content().then(setContentMetrics).catch(() => {});
    apiClient.admin.agents().then(setAgentsList).catch(() => {});
    apiClient.admin.agentLog(20).then(setAgentLog).catch(() => {});
    apiClient.admin.insights('pending').then(setPendingInsights).catch(() => {});
  }, [user?.isAdmin]);

  // Fetch user-specific data
  useEffect(() => {
    if (!user?.id) return;
    // Reading stats - no direct REST endpoint, set null for now
    setUserReadingStats(null);
    setLibraryProgress(null);
  }, [user?.id]);

  // Helper to refetch data after mutations
  const refetchBooks = () => apiClient.dashboard.books()
    .catch(() => apiClient.books.list())
    .then(async (dashBooks: any[]) => {
      if (!dashBooks) return;
      const enriched = await Promise.all(dashBooks.map(async (b: any) => {
        try {
          const full = await apiClient.books.getBySlug(b.slug);
          const rawCover = full?.coverUrl || (full as any)?.cover_url || b.coverUrl || b.cover_url;
          return { ...b, chapters: full?.chapters ?? [], materials: full?.materials ?? [], coverUrl: rawCover ? mediaUrl(rawCover) : coverForSlug(b.slug) };
        } catch {
          const rawCover = b.coverUrl || b.cover_url;
          return { ...b, chapters: [], materials: [], coverUrl: rawCover ? mediaUrl(rawCover) : coverForSlug(b.slug) };
        }
      }));
      setBooksData(enriched);
    }).catch(() => {});
  const refetchMaterials = () => apiClient.dashboard.materials().then(setMaterialsData).catch(() => {});

  // Admin status from API — supports all 4 admin emails
  const isAdmin = user?.isAdmin === true;

  // Show admin UI only if user is admin AND viewing as admin
  const showAsAdmin = isAdmin && viewMode === 'admin';

  // Loading state — block on auth before rendering anything else
  if (authLoading) {
    return (
      <div className="dashboard-loading">
        <div className="text-center">
          <Loader2 size={40} className="dashboard-loading-spinner" />
          <p className="dashboard-loading-text">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Belt-and-suspenders auth gate (in addition to ProtectedRoute in App.tsx)
  // Prevents the dashboard from ever rendering for unauthenticated visitors
  // even if upstream routing logic is bypassed.
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const handleDeleteBook = async (bookId: string) => {
    if (!isAdmin) return;
    if (confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      await apiClient.adminBooks.delete(bookId);
      refetchBooks();
      if (selectedBook === bookId) setSelectedBook(null);
    }
  };

  const handleAddBook = async () => {
    if (!isAdmin) return;
    setIsSubmitting(true);
    setUploadProgress('Starting upload...');

    try {
      let coverStorageId: string | undefined;
      let chapters = parsedChapters;

      // Upload manuscript if provided
      if (manuscriptFile) {
        setUploadProgress('Uploading manuscript...');
        const { url: uploadUrl } = await apiClient.uploads.generateUploadUrl();
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': manuscriptFile.type },
          body: manuscriptFile,
        });
        const { storageId } = await response.json();

        setUploadProgress('Parsing manuscript...');
        const parseResult = await apiClient.uploads.parseManuscript(storageId, manuscriptFile.type);

        if ('error' in parseResult) {
          alert(`Error parsing manuscript: ${(parseResult as any).error}`);
          setIsSubmitting(false);
          setUploadProgress('');
          return;
        }

        // Use parsed data
        const parsed = parseResult as { title: string; chapters: any[] };
        if (!newBookTitle) setNewBookTitle(parsed.title);
        chapters = parsed.chapters;
        setParsedChapters(chapters);
      }

      // Upload cover if provided
      if (coverFile) {
        setUploadProgress('Uploading cover image...');
        const { url: uploadUrl } = await apiClient.uploads.generateUploadUrl();
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': coverFile.type },
          body: coverFile,
        });
        const { storageId } = await response.json();
        coverStorageId = storageId;
      }

      // Create book with chapters
      const title = newBookTitle || 'Untitled Book';
      const slug = newBookSlug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      setUploadProgress('Creating book...');

      if (chapters && chapters.length > 0) {
        await apiClient.adminBooks.createFromManuscript({
          title,
          subtitle: newBookSubtitle || undefined,
          slug,
          coverStorageId,
          chapters,
        });
      } else {
        // Create empty book if no manuscript
        await apiClient.adminBooks.create({
          title,
          subtitle: newBookSubtitle || undefined,
          slug,
        });
      }

      refetchBooks();

      // Reset form
      setNewBookTitle('');
      setNewBookSubtitle('');
      setNewBookSlug('');
      setManuscriptFile(null);
      setCoverFile(null);
      setParsedChapters(null);
      setShowAddBook(false);
      setUploadProgress('');
    } catch (error: any) {
      console.error('Error creating book:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleToggleBookVisibility = async (bookId: string, currentStatus: string) => {
    if (!isAdmin) return;
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    await apiClient.adminBooks.setVisibility(bookId, newStatus);
    refetchBooks();
  };

  const handleUploadCover = async (bookId: string, file: File) => {
    if (!isAdmin) return;
    try {
      const { url: uploadUrl } = await apiClient.uploads.generateUploadUrl();
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      const { storageId } = await response.json();
      await apiClient.adminBooks.uploadCover(bookId, storageId);
      refetchBooks();
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleAddMaterial = async () => {
    if (!isAdmin || !newMaterialName) return;
    setIsSubmitting(true);
    setUploadProgress('Uploading material...');

    try {
      if (materialFile) {
        // Upload file to storage
        const { url: uploadUrl } = await apiClient.uploads.generateUploadUrl();
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': materialFile.type },
          body: materialFile,
        });
        const { storageId } = await response.json();

        // Determine file type
        const fileExt = materialFile.name.split('.').pop()?.toLowerCase();
        let fileType: 'pdf' | 'video' | 'guide' | 'template' = 'pdf';
        if (fileExt === 'mp4' || fileExt === 'mov' || fileExt === 'webm') {
          fileType = 'video';
        }

        // Format file size
        const fileSizeKB = Math.round(materialFile.size / 1024);
        const fileSize = fileSizeKB > 1024
          ? `${(fileSizeKB / 1024).toFixed(1)} MB`
          : `${fileSizeKB} KB`;

        await apiClient.adminMaterials.upload({
          name: newMaterialName,
          description: newMaterialDesc || undefined,
          type: fileType,
          bookId: selectedBookForMaterial || undefined,
          storageId,
          fileSize,
        });
      } else if (newMaterialUrl) {
        // Use URL directly (legacy support)
        await apiClient.adminMaterials.create({
          name: newMaterialName,
          description: newMaterialDesc || undefined,
          type: 'pdf',
          fileUrl: newMaterialUrl,
        });
      } else {
        alert('Please upload a file or provide a URL');
        setIsSubmitting(false);
        setUploadProgress('');
        return;
      }

      // Reset form
      setNewMaterialName('');
      setNewMaterialDesc('');
      setNewMaterialUrl('');
      setMaterialFile(null);
      setSelectedBookForMaterial(null);
      setShowAddMaterial(false);
      setUploadProgress('');
    } catch (error: any) {
      console.error('Error adding material:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleDeleteMaterial = async (materialId: string) => {
    if (!isAdmin) return;
    if (confirm('Are you sure you want to delete this material?')) {
      await apiClient.adminMaterials.delete(materialId);
      refetchMaterials();
    }
  };

  // Chapter management handlers
  const handleAddChapter = async () => {
    if (!isAdmin || !selectedBook || !newChapterTitle.trim()) return;
    setIsSubmitting(true);
    try {
      await apiClient.adminBooks.addChapter(selectedBook, {
        title: newChapterTitle.trim(),
        content: newChapterContent.trim() || 'Chapter content goes here...',
      });
      setNewChapterTitle('');
      setNewChapterContent('');
      setShowAddChapter(false);
      refetchBooks();
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateChapter = async () => {
    if (!isAdmin || !editingChapter) return;
    setIsSubmitting(true);
    try {
      await apiClient.adminBooks.updateChapter(editingChapter.id, {
        title: editingChapter.title,
        content: editingChapter.content,
        isFreePreview: editingChapter.isFreePreview,
      });
      setEditingChapter(null);
      refetchBooks();
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteChapter = async (chapterId: string) => {
    if (!isAdmin) return;
    if (confirm('Are you sure you want to delete this chapter? This action cannot be undone.')) {
      try {
        await apiClient.adminBooks.deleteChapter(chapterId);
        refetchBooks();
      } catch (error: any) {
        alert(`Error: ${error.message}`);
      }
    }
  };

  const selectedBookData = books.find(b => b.id === selectedBook);

  return (
    <div className="dashboard-page">
      {/* Top Navigation */}
      <header className="dashboard-top-header">
        <div className="dashboard-top-header-inner">
          {/* Left: Logo */}
          <Link to="/" className="dashboard-logo-link">
            <Zap size={24} color="var(--accent)" />
            <span className="dashboard-logo-text">
              Fasting Carnivore
            </span>
          </Link>

          {/* Center: Search */}
          <div className="dashboard-search-wrap">
            <Search size={16} className="dashboard-search-icon" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="dashboard-search-input"
            />
          </div>

          {/* Right: User */}
          <div className="dashboard-header-actions">
            {isAdmin && (
              <button
                onClick={() => setViewMode(viewMode === 'admin' ? 'customer' : 'admin')}
                className={`dashboard-view-toggle ${viewMode === 'admin' ? 'dashboard-view-toggle--admin' : 'dashboard-view-toggle--customer'}`}
              >
                {viewMode === 'admin' ? (
                  <>
                    <Shield size={16} />
                    Admin View
                  </>
                ) : (
                  <>
                    <Eye size={16} />
                    Customer View
                  </>
                )}
              </button>
            )}

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="dashboard-avatar-btn"
              >
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    onClick={() => setShowUserMenu(false)}
                    className="dashboard-dropdown-backdrop"
                  />
                  <div className="dashboard-dropdown">
                    {/* User Info Header */}
                    <div className="dashboard-dropdown-header">
                      <div className="dashboard-dropdown-user-row">
                        <div className="dashboard-avatar-lg">
                          {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="dashboard-dropdown-name">
                            {user?.name || 'Member'}
                          </p>
                          <p className="dashboard-dropdown-email">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="dashboard-dropdown-section">
                      <Link
                        to="/account"
                        onClick={() => setShowUserMenu(false)}
                        className="dropdown-menu-item dashboard-dropdown-item"
                      >
                        <Users size={18} color="#666" />
                        <div>
                          <span className="font-medium">Profile</span>
                          <p className="dashboard-dropdown-hint">View your profile</p>
                        </div>
                      </Link>

                      <Link
                        to="/account?tab=security"
                        onClick={() => setShowUserMenu(false)}
                        className="dropdown-menu-item dashboard-dropdown-item"
                      >
                        <Settings size={18} color="#666" />
                        <div>
                          <span className="font-medium">Account Settings</span>
                          <p className="dashboard-dropdown-hint">Security & preferences</p>
                        </div>
                      </Link>

                      <Link
                        to="/account?tab=billing"
                        onClick={() => setShowUserMenu(false)}
                        className="dropdown-menu-item dashboard-dropdown-item"
                      >
                        <DollarSign size={18} color="#666" />
                        <div>
                          <span className="font-medium">Billing & Payments</span>
                          <p className="dashboard-dropdown-hint">Update credit card</p>
                        </div>
                      </Link>
                    </div>

                    {/* Divider */}
                    <div className="dashboard-dropdown-divider" />

                    {/* Logout */}
                    <div className="dashboard-dropdown-section">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        className="dropdown-menu-item dashboard-dropdown-logout"
                      >
                        <LogOut size={18} />
                        <span className="font-medium">Sign Out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay open"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Menu Toggle Button */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      <div className="dashboard-container dashboard-layout">
        {/* Sidebar */}
        <aside className={`dashboard-sidebar dashboard-sidebar-sticky ${sidebarOpen ? 'open' : ''}`}>
          <nav className="dashboard-sidebar-nav">
            <NavItem
              icon={<BookOpen size={18} />}
              label="Books"
              active={activeTab === 'books' && !selectedBook}
              onClick={() => { setActiveTab('books'); setSelectedBook(null); }}
              badge={books.length}
            />
            {showAsAdmin && (
              <NavItem
                icon={<Gift size={18} />}
                label="Materials"
                active={activeTab === 'materials'}
                onClick={() => setActiveTab('materials')}
                badge={materials.length}
              />
            )}
            {showAsAdmin && (
              <NavItem
                icon={<Users size={18} />}
                label="Members"
                active={activeTab === 'members'}
                onClick={() => setActiveTab('members')}
                badge={members?.length}
              />
            )}
            {showAsAdmin && (
              <>
                <div className="dashboard-sidebar-spacer" />
                <p className="dashboard-sidebar-label">
                  Business
                </p>
                <NavItem
                  icon={<UserCheck size={18} />}
                  label="Customers"
                  active={activeTab === 'customers'}
                  onClick={() => setActiveTab('customers')}
                  badge={customerStats?.totalCustomers}
                />
                <NavItem
                  icon={<Radio size={18} />}
                  label="Broadcast"
                  active={activeTab === 'broadcast'}
                  onClick={() => setActiveTab('broadcast')}
                />
                <NavItem
                  icon={<Share2 size={18} />}
                  label="Affiliates"
                  active={activeTab === 'affiliates'}
                  onClick={() => setActiveTab('affiliates')}
                  badge={affiliates?.length}
                />
                <NavItem
                  icon={<TrendingUp size={18} />}
                  label="Analytics"
                  active={activeTab === 'analytics'}
                  onClick={() => setActiveTab('analytics')}
                />
                <NavItem
                  icon={<Activity size={18} />}
                  label="Agents"
                  active={activeTab === 'agents'}
                  onClick={() => setActiveTab('agents')}
                  badge={pendingInsights?.length || undefined}
                />
              </>
            )}
          </nav>

          {/* Customer Library Navigation - Expandable Books */}
          {activeTab === 'books' && (
            <div className="dashboard-library-section">
              {/* Reading Streak */}
              {libraryProgress && libraryProgress.readingStreak > 0 && (
                <div className="dashboard-streak-badge">
                  <Flame size={16} color="#f59e0b" />
                  <span className="dashboard-streak-text">
                    {libraryProgress?.readingStreak || 0} day streak!
                  </span>
                </div>
              )}

              {/* Continue Reading */}
              {userReadingStats?.lastReadBook && (
                <button
                  onClick={() => window.location.href = `/reader/${userReadingStats.lastReadBook!.slug}?chapter=${userReadingStats.lastReadBook!.currentChapter}`}
                  className="dashboard-continue-btn"
                >
                  <PlayCircle size={16} />
                  <div className="dashboard-continue-info">
                    <div className="dashboard-continue-label">Continue Reading</div>
                    <div className="dashboard-continue-chapter">
                      {userReadingStats.lastReadBook.chapterTitle}
                    </div>
                  </div>
                </button>
              )}

              <p className="dashboard-sidebar-label">
                My Library
              </p>
              {filteredBooks.filter(b => b.status === 'published').map((book) => {
                const bookProg = libraryProgress?.bookProgress?.[book.id];
                const completedCount = bookProg?.completedChapters?.length || 0;
                const totalChapters = book.chapters.length;
                const progressPercent = totalChapters > 0 ? Math.round((completedCount / totalChapters) * 100) : 0;

                return (
                  <div key={book.id}>
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedBooks);
                        if (newExpanded.has(book.id)) {
                          newExpanded.delete(book.id);
                        } else {
                          newExpanded.add(book.id);
                        }
                        setExpandedBooks(newExpanded);
                        setSelectedBook(book.id);
                      }}
                      className={`dashboard-book-btn ${selectedBook === book.id ? 'dashboard-book-btn--active' : ''}`}
                    >
                      <div className="dashboard-book-row">
                        {expandedBooks.has(book.id) ? (
                          <ChevronDown size={14} color="#666" />
                        ) : (
                          <ChevronRight size={14} color="#666" />
                        )}
                        <BookOpen size={16} color="var(--accent)" />
                        <span className="dashboard-book-title">
                          {book.title}
                        </span>
                        <span className="dashboard-book-count">
                          {completedCount}/{totalChapters}
                        </span>
                      </div>
                      {/* Progress Bar */}
                      <div className="dashboard-progress-track">
                        <div
                          className={`dashboard-progress-fill ${progressPercent === 100 ? 'dashboard-progress-fill--complete' : ''}`}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </button>

                    {/* Sub-sections - always visible */}
                    <div className="dashboard-subsections">
                      {/* CHAPTERS Section - expandable */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newExpanded = new Set(expandedBooks);
                          if (newExpanded.has(book.id)) {
                            newExpanded.delete(book.id);
                          } else {
                            newExpanded.add(book.id);
                          }
                          setExpandedBooks(newExpanded);
                        }}
                        className="dashboard-section-toggle"
                      >
                        {expandedBooks.has(book.id) ? (
                          <ChevronDown size={12} color="#999" />
                        ) : (
                          <ChevronRight size={12} color="#999" />
                        )}
                        <span className="dashboard-section-label-text">
                          Chapters
                        </span>
                        <span className="dashboard-section-count">
                          ({book.chapters.length})
                        </span>
                      </button>

                      {/* Expanded chapters list */}
                      {expandedBooks.has(book.id) && (
                        <div className="dashboard-chapter-list">
                          {book.chapters.map((chapter) => {
                            const isCompleted = bookProg?.completedChapters?.includes(chapter.number);
                            const discussionCount = libraryProgress?.chapterDiscussionCounts?.[chapter.id] || 0;

                            return (
                              <button
                                key={chapter.id}
                                onClick={() => {
                                  setSelectedChapter({ bookId: book.id, chapterId: chapter.id, chapterNumber: chapter.number });
                                  setSelectedBook(book.id);
                                }}
                                className={`dashboard-chapter-btn ${selectedChapter?.chapterId === chapter.id ? 'dashboard-chapter-btn--active' : ''}`}
                              >
                                {isCompleted ? (
                                  <CheckCircle size={14} color={selectedChapter?.chapterId === chapter.id ? 'white' : '#22c55e'} />
                                ) : (
                                  <span className={`dashboard-chapter-number ${selectedChapter?.chapterId === chapter.id ? 'dashboard-chapter-number--active' : ''}`}>
                                    {chapter.number}
                                  </span>
                                )}
                                <span className={`dashboard-chapter-title ${selectedChapter?.chapterId === chapter.id ? 'dashboard-chapter-title--active' : ''}`}>
                                  {chapter.title}
                                </span>
                                {discussionCount > 0 && (
                                  <span className={`dashboard-chapter-discussion ${selectedChapter?.chapterId === chapter.id ? 'dashboard-chapter-discussion--active' : ''}`}>
                                    <MessageCircle size={8} /> {discussionCount}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* WORKBOOKS Section - expandable, always visible */}
                      {materials.filter(m => m.bookId === book.id).length > 0 && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const newExpanded = new Set(expandedWorkbooks);
                              if (newExpanded.has(book.id)) {
                                newExpanded.delete(book.id);
                              } else {
                                newExpanded.add(book.id);
                              }
                              setExpandedWorkbooks(newExpanded);
                            }}
                            className="dashboard-section-toggle"
                          >
                            {expandedWorkbooks.has(book.id) ? (
                              <ChevronDown size={12} color="#999" />
                            ) : (
                              <ChevronRight size={12} color="#999" />
                            )}
                            <span className="dashboard-section-label-text">
                              Workbooks
                            </span>
                            <span className="dashboard-section-count">
                              ({materials.filter(m => m.bookId === book.id).length})
                            </span>
                          </button>

                          {/* Expanded workbooks list */}
                          {expandedWorkbooks.has(book.id) && (
                            <div className="dashboard-chapter-list">
                              {materials.filter(m => m.bookId === book.id).map((material) => {
                                const isSelected = selectedWorkbook?.id === material.id;
                                return (
                                  <button
                                    key={material.id}
                                    onClick={() => {
                                      setSelectedWorkbook({
                                        id: material.id,
                                        name: material.name,
                                        description: material.description,
                                        type: material.type,
                                        fileUrl: material.fileUrl,
                                        bookId: material.bookId,
                                      });
                                      setSelectedChapter(null); // Clear chapter selection
                                    }}
                                    className={`dashboard-chapter-btn ${isSelected ? 'dashboard-chapter-btn--active' : ''}`}
                                  >
                                    <FileText size={12} color={isSelected ? 'white' : material.type === 'pdf' ? '#ef4444' : material.type === 'video' ? '#8b5cf6' : '#3b82f6'} />
                                    <span className={`dashboard-chapter-title ${isSelected ? 'dashboard-chapter-title--active' : ''}`}>
                                      {material.name}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}

                      {/* HIGHLIGHTS Section */}
                      {bookProg && bookProg.highlightsCount > 0 && (
                        <Link
                          to={`/reader/${book.slug}`}
                          className="dashboard-section-toggle"
                        >
                          <Highlighter size={12} color="#fbbf24" />
                          <span className="dashboard-section-label-text">
                            Highlights
                          </span>
                          <span className="dashboard-section-count">
                            ({bookProg?.highlightsCount || 0})
                          </span>
                        </Link>
                      )}

                      {/* NOTES Section */}
                      {bookProg && bookProg.notesCount > 0 && (
                        <Link
                          to={`/reader/${book.slug}`}
                          className="dashboard-section-toggle"
                        >
                          <StickyNote size={12} color="#a3e635" />
                          <span className="dashboard-section-label-text">
                            Notes
                          </span>
                          <span className="dashboard-section-count">
                            ({bookProg?.notesCount || 0})
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Admin Actions */}
          {showAsAdmin && (
            <div className="dashboard-admin-actions">
              <p className="dashboard-sidebar-label">
                Admin Actions
              </p>
              <button
                onClick={() => setShowAddBook(true)}
                className="dashboard-admin-btn-primary"
              >
                <Plus size={16} /> New Book
              </button>
              <button
                onClick={() => setShowAddMaterial(true)}
                className="dashboard-admin-btn-secondary"
              >
                <Upload size={16} /> Upload Material
              </button>
            </div>
          )}

          {/* Bottom */}
          <div className="dashboard-sidebar-bottom">
            <NavItem icon={<Settings size={18} />} label="Settings" onClick={() => window.location.href = '/account'} />
            <NavItem icon={<LogOut size={18} />} label="Sign Out" onClick={() => logout()} />
          </div>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main-content">
          <AnimatePresence mode="wait">
            {activeTab === 'books' && (
              <motion.div
                key="books"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                {/* Header copied from Overview */}
                <div className="mb-6">
                  <h1 className="dashboard-page-title">
                    Welcome back, {user?.name || 'there'}
                  </h1>
                  <p className="dashboard-page-subtitle">
                    {isAdmin ? "Here's what's happening with your content." : "Your reading journey continues here."}
                  </p>
                </div>

                {/* Stats copied from Overview */}
                <div className="dashboard-stats-grid">
                  {showAsAdmin ? (
                    <>
                      <StatCard icon={<Users size={20} />} label="Total Users" value={(analytics.totalUsers ?? 0).toLocaleString()} change={0} />
                      <StatCard icon={<BookMarked size={20} />} label="Active Readers" value={(analytics.activeReaders ?? 0).toLocaleString()} change={0} />
                      <StatCard icon={<Layers size={20} />} label="Books" value={(analytics.totalBooks ?? 0).toLocaleString()} change={0} />
                      <StatCard icon={<Target size={20} />} label="Purchased" value={(analytics.purchasedUsers ?? 0).toLocaleString()} change={0} />
                    </>
                  ) : (
                    <>
                      <StatCard icon={<BookOpen size={20} />} label="Books in Library" value={(userReadingStats?.totalBooks || books.length).toString()} />
                      <StatCard icon={<Target size={20} />} label="Completed" value={(userReadingStats?.booksCompleted || 0).toString()} />
                      <StatCard icon={<Layers size={20} />} label="Chapters Read" value={(userReadingStats?.totalChaptersRead || 0).toString()} />
                      <StatCard icon={<Star size={20} />} label="Highlights" value={(userReadingStats?.highlightsCount || 0).toString()} />
                      <StatCard icon={<MessageSquare size={20} />} label="Notes" value={(userReadingStats?.notesCount || 0).toString()} />
                    </>
                  )}
                </div>

                {/* WORKBOOK WORKSPACE - Shows for both admin and customer when workbook selected */}
                {selectedWorkbook && user?.id && (
                  <WorkbookWorkspace
                    workbook={selectedWorkbook}
                    userId={user.id}
                    onClose={() => setSelectedWorkbook(null)}
                  />
                )}

                {/* ADMIN VIEW */}
                {showAsAdmin && !selectedWorkbook && (
                  <>
                    <div className="dashboard-section-header">
                      <div>
                        <h1 className="dashboard-page-title">
                          Books
                        </h1>
                        <p className="dashboard-page-subtitle">
                          Manage your book content and chapters
                        </p>
                      </div>
                      <button
                        onClick={() => setShowAddBook(true)}
                        className="dashboard-action-btn-primary"
                      >
                        <Plus size={18} /> New Book
                      </button>
                    </div>

                    {books.length === 0 ? (
                      <Card>
                        <div className="dashboard-empty-state">
                          <BookOpen size={48} color="#ccc" className="mb-4" />
                          <h3 className="dashboard-empty-title">No books yet</h3>
                          <p className="dashboard-empty-text">Create your first book to get started.</p>
                          <button onClick={() => setShowAddBook(true)} className="btn btn-primary">
                            <Plus size={18} /> Create Book
                          </button>
                        </div>
                      </Card>
                    ) : (
                      <div className="dashboard-books-grid">
                        {filteredBooks.map((book, i) => (
                          <BookCard
                            key={book.id}
                            book={book}
                            index={i}
                            isAdmin={true}
                            isSelected={selectedBook === book.id}
                            onSelect={() => setSelectedBook(selectedBook === book.id ? null : book.id)}
                            onDelete={() => handleDeleteBook(book.id)}
                            onToggleVisibility={() => handleToggleBookVisibility(book.id, book.status)}
                            onUploadCover={(file) => handleUploadCover(book.id, file)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Chapter Panel - Admin */}
                    <AnimatePresence>
                      {selectedBookData && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="dashboard-chapter-panel"
                        >
                          <Card
                            title={`${selectedBookData.title} — Chapters`}
                            subtitle={`${selectedBookData.chapters.length} chapters`}
                            action={{ label: 'Add Chapter', onClick: () => setShowAddChapter(true), icon: <Plus size={16} /> }}
                          >
                            {selectedBookData.chapters.length === 0 ? (
                              <div className="dashboard-empty-sm">
                                <FileText size={32} color="#ccc" className="mb-3" />
                                <p className="dashboard-empty-text-sm">No chapters yet.</p>
                              </div>
                            ) : (
                              <div className="flex-col gap-2">
                                {selectedBookData.chapters.map((chapter) => (
                                  <ChapterRow
                                    key={chapter.id}
                                    chapter={chapter}
                                    isAdmin={true}
                                    onEdit={() => setEditingChapter({
                                      id: chapter.id,
                                      title: chapter.title,
                                      content: chapter.content || '',
                                      isFreePreview: chapter.isFreePreview || false,
                                    })}
                                    onDelete={() => handleDeleteChapter(chapter.id)}
                                  />
                                ))}
                              </div>
                            )}
                          </Card>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}

                {/* CUSTOMER VIEW - Book Cards */}
                {!showAsAdmin && !selectedWorkbook && (
                  <>
                    {filteredBooks.filter(b => b.status === 'published').length === 0 ? (
                      <Card>
                        <div className="dashboard-empty-state">
                          <BookOpen size={48} color="#ccc" className="mb-4" />
                          <h3 className="dashboard-empty-title">No books available</h3>
                          <p className="dashboard-empty-text">Check back soon for new content.</p>
                        </div>
                      </Card>
                    ) : (
                      <div className="dashboard-books-grid">
                        {filteredBooks
                          .filter(b => b.status === 'published')
                          .filter(b => !selectedBook || b.id === selectedBook)
                          .map((book, i) => (
                            <BookCard
                              key={book.id}
                              book={book}
                              index={i}
                              isAdmin={false}
                              isSelected={selectedBook === book.id}
                              onSelect={() => setSelectedBook(book.id)}
                              onDelete={() => {}}
                            />
                          ))}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}


            {activeTab === 'materials' && (
              <motion.div
                key="materials"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="dashboard-section-header">
                  <div>
                    <h1 className="dashboard-page-title">
                      Bonus Materials
                    </h1>
                    <p className="dashboard-page-subtitle">
                      PDFs, guides, and downloadable resources
                    </p>
                  </div>
                  {showAsAdmin && (
                    <button
                      onClick={() => setShowAddMaterial(true)}
                      className="dashboard-action-btn-amber"
                    >
                      <Upload size={18} /> Upload
                    </button>
                  )}
                </div>

                {materials.length === 0 ? (
                  <Card>
                    <div className="dashboard-empty-state">
                      <Gift size={48} color="#ccc" className="mb-4" />
                      <h3 className="dashboard-empty-title">No materials yet</h3>
                      <p className="dashboard-empty-text">Upload PDFs, guides, and other resources for your members.</p>
                    </div>
                  </Card>
                ) : (
                  <div className="flex-col gap-8">
                    {/* Group materials by book */}
                    {(() => {
                      // Group materials by bookId
                      const grouped = materials.reduce((acc: Record<string, typeof materials>, mat) => {
                        const key = mat.bookId || 'global';
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(mat);
                        return acc;
                      }, {});

                      // Get unique book titles
                      const bookGroups = Object.entries(grouped).map(([bookId, mats]) => ({
                        bookId,
                        bookTitle: bookId === 'global' ? 'General Resources' : (mats[0] as any).bookTitle || 'Unknown Book',
                        materials: mats,
                      }));

                      // Sort: books first (alphabetically), then global
                      bookGroups.sort((a, b) => {
                        if (a.bookId === 'global') return 1;
                        if (b.bookId === 'global') return -1;
                        return a.bookTitle.localeCompare(b.bookTitle);
                      });

                      return bookGroups.map((group, groupIndex) => (
                        <div key={group.bookId}>
                          <div className="dashboard-material-group-header">
                            <BookMarked size={20} color="var(--primary)" />
                            <h2 className="dashboard-material-group-title">
                              {group.bookTitle}
                            </h2>
                            <span className="dashboard-material-group-count">
                              {group.materials.length} {group.materials.length === 1 ? 'resource' : 'resources'}
                            </span>
                          </div>
                          <div className="dashboard-materials-grid">
                            {group.materials.map((material, i) => (
                              <MaterialCard
                                key={material.id}
                                material={material}
                                index={groupIndex * 10 + i}
                                isAdmin={showAsAdmin}
                                onDelete={() => handleDeleteMaterial(material.id)}
                                onView={() => setViewingMaterial({
                                  id: material.id,
                                  name: material.name,
                                  description: material.description,
                                  type: material.type,
                                  fileUrl: material.fileUrl,
                                  fileSize: material.size,
                                })}
                              />
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'members' && (
              <motion.div
                key="members"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="mb-6">
                  <h1 className="dashboard-page-title">
                    Members
                  </h1>
                  <p className="dashboard-page-subtitle">
                    {members?.length || 0} total members
                  </p>
                </div>

                {!members || members.length === 0 ? (
                  <Card>
                    <div className="dashboard-empty-state">
                      <Users size={48} color="#ccc" className="mb-4" />
                      <h3 className="dashboard-empty-title">No members yet</h3>
                      <p className="dashboard-empty-text">Members will appear here after they purchase.</p>
                    </div>
                  </Card>
                ) : (
                  <Card>
                    <div className="flex-col gap-2">
                      {members.map((member) => (
                        <div key={member.id} className="dashboard-member-row">
                          <div className="dashboard-member-avatar">
                            {(member.name || member.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <h4 className="dashboard-member-name">
                              {member.name || 'No name'}
                            </h4>
                            <p className="dashboard-member-email">
                              {member.email}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="dashboard-member-stat">
                              {member.highlight_count ?? 0} highlights
                            </p>
                            <p className="dashboard-member-date">
                              Joined {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </motion.div>
            )}

            {/* Customers Tab - Admin Only */}
            {activeTab === 'customers' && showAsAdmin && (
              <motion.div
                key="customers"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="mb-6">
                  <h1 className="dashboard-page-title">
                    Customers
                  </h1>
                  <p className="dashboard-page-subtitle">
                    {customerStats?.totalMembers || 0} paid customers
                  </p>
                </div>

                {/* Customer Stats */}
                <div className="dashboard-stats-grid-4">
                  <StatCard icon={<UserCheck size={20} />} label="Total Customers" value={String(customerStats?.totalMembers || 0)} change={0} />
                  <StatCard icon={<TrendingUp size={20} />} label="New This Month" value={String(0)} change={0} />
                  <StatCard icon={<Activity size={20} />} label="Active This Week" value={String(customerStats?.activeReaders || 0)} change={0} />
                  <StatCard icon={<MessageCircle size={20} />} label="Discord Linked" value={String(0)} change={0} />
                </div>

                {/* Customer List */}
                <Card title="All Customers">
                  {!customers || customers.length === 0 ? (
                    <div className="dashboard-empty-state">
                      <UserCheck size={48} color="#ccc" className="mb-4" />
                      <h3 className="dashboard-empty-title">No customers yet</h3>
                      <p className="dashboard-empty-text">Customers will appear here after they purchase.</p>
                    </div>
                  ) : (
                    <div className="flex-col gap-2">
                      {customers.map((customer) => (
                        <div key={customer.id} className="dashboard-member-row">
                          <div className={`dashboard-member-avatar ${!(customer.discord_linked ?? customer.discordLinked) ? 'dashboard-member-avatar--inactive' : ''}`}>
                            {(customer.name || customer.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <h4 className="dashboard-member-name">
                              {customer.name || 'No name'}
                            </h4>
                            <p className="dashboard-member-email">
                              {customer.email}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {(customer.discord_linked ?? customer.discordLinked) && (
                              <span className="dashboard-badge-discord">
                                Discord
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="dashboard-member-stat">
                              {(customer.purchased_at ?? customer.purchasedAt) ? new Date(customer.purchased_at ?? customer.purchasedAt).toLocaleDateString() : 'N/A'}
                            </p>
                            <p className="dashboard-member-date">
                              Last active: {(customer.last_login_at ?? customer.lastLoginAt) ? new Date(customer.last_login_at ?? customer.lastLoginAt).toLocaleDateString() : 'Never'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </motion.div>
            )}

            {/* Broadcast Tab - Admin Only */}
            {activeTab === 'broadcast' && showAsAdmin && (
              <motion.div
                key="broadcast"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="mb-6">
                  <h1 className="dashboard-page-title">
                    Broadcast Messages
                  </h1>
                  <p className="dashboard-page-subtitle">
                    Send messages to all your customers
                  </p>
                </div>

                <div className="mb-6">
                  {/* Discord Broadcast */}
                  <BroadcastCard
                    icon={<MessageCircle size={24} />}
                    title="Discord Broadcast"
                    description={`Send DM to customers with Discord linked`}
                  />
                </div>

                {/* Broadcast History */}
                <Card title="Recent Broadcasts">
                  {!broadcastHistory || broadcastHistory.length === 0 ? (
                    <div className="dashboard-empty-sm">
                      <Radio size={36} color="#ccc" className="mb-3" />
                      <p className="dashboard-empty-text-sm">No broadcasts sent yet</p>
                    </div>
                  ) : (
                    <div className="flex-col gap-2">
                      {broadcastHistory.map((broadcast) => (
                        <div key={broadcast.id} className="dashboard-broadcast-row">
                          <MessageCircle size={18} color="var(--primary)" />
                          <div className="flex-1">
                            <p className="dashboard-broadcast-msg">
                              {broadcast.message}
                            </p>
                            <p className="dashboard-broadcast-date">
                              {(broadcast.created_at ?? broadcast.createdAt) ? new Date(broadcast.created_at ?? broadcast.createdAt).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="dashboard-badge-success">
                              {broadcast.sent} sent
                            </span>
                            {broadcast.failed > 0 && (
                              <span className="dashboard-badge-error">
                                {broadcast.failed} failed
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </motion.div>
            )}

            {/* Affiliates Tab - Admin Only */}
            {activeTab === 'affiliates' && showAsAdmin && (
              <motion.div
                key="affiliates"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="mb-6">
                  <h1 className="dashboard-page-title">
                    Affiliate Program
                  </h1>
                  <p className="dashboard-page-subtitle">
                    Manage affiliates and track referral performance
                  </p>
                </div>

                {/* Affiliate Stats */}
                <div className="dashboard-stats-grid-4">
                  <StatCard icon={<Share2 size={20} />} label="Total Affiliates" value={String(affiliateStats?.totalAffiliates || 0)} change={0} />
                  <StatCard icon={<Target size={20} />} label="Total Clicks" value={String(affiliateStats?.totalClicks || 0)} change={0} />
                  <StatCard icon={<UserCheck size={20} />} label="Conversions" value={String(affiliateStats?.totalConversions || 0)} change={0} />
                  <StatCard icon={<DollarSign size={20} />} label="Total Earnings" value={`$${(affiliateStats?.totalEarnings || 0).toFixed(2)}`} change={0} />
                </div>

                {/* Affiliate List */}
                <Card title="All Affiliates">
                  {!affiliates || affiliates.length === 0 ? (
                    <div className="dashboard-empty-state">
                      <Share2 size={48} color="#ccc" className="mb-4" />
                      <h3 className="dashboard-empty-title">No affiliates yet</h3>
                      <p className="dashboard-empty-text">Affiliates will appear here once you create them.</p>
                    </div>
                  ) : (
                    <div className="flex-col gap-2">
                      {affiliates.map((affiliate) => (
                        <AffiliateRow key={affiliate.id} affiliate={affiliate} />
                      ))}
                    </div>
                  )}
                </Card>

                {/* Pending Payouts */}
                {affiliateStats && affiliateStats.pendingPayouts > 0 && (
                  <Card title="Pending Payouts" subtitle={`$${affiliateStats.pendingPayouts.toFixed(2)} total`}>
                    <p className="dashboard-empty-text-sm">
                      Review and process affiliate payouts in the full affiliate management panel.
                    </p>
                  </Card>
                )}
              </motion.div>
            )}

            {/* Analytics Tab - Admin Only */}
            {activeTab === 'analytics' && showAsAdmin && (
              <motion.div
                key="analytics"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="mb-6">
                  <h1 className="dashboard-page-title">
                    Analytics Dashboard
                  </h1>
                  <p className="dashboard-page-subtitle">
                    Track revenue, engagement, and content performance
                  </p>
                </div>

                {/* Revenue Metrics */}
                <Card title="Revenue" subtitle="Lifetime and monthly performance">
                  <div className="dashboard-stats-grid-4">
                    <div className="dashboard-metric-cell">
                      <p className="dashboard-metric-label">Total Revenue</p>
                      <p className="dashboard-metric-value">
                        ${revenueMetrics?.totalRevenue?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="dashboard-metric-cell">
                      <p className="dashboard-metric-label">This Month</p>
                      <p className="dashboard-metric-value dashboard-metric-value--accent">
                        ${revenueMetrics?.revenueThisMonth?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="dashboard-metric-cell">
                      <p className="dashboard-metric-label">Avg Order</p>
                      <p className="dashboard-metric-value">
                        ${revenueMetrics?.avgOrderValue?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="dashboard-metric-cell">
                      <p className="dashboard-metric-label">Customer LTV</p>
                      <p className="dashboard-metric-value">
                        ${revenueMetrics?.ltv?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Engagement Metrics */}
                <Card title="Engagement" subtitle="User activity and retention">
                  <div className="dashboard-stats-grid-4">
                    <div className="dashboard-metric-cell">
                      <p className="dashboard-metric-label">Total Customers</p>
                      <p className="dashboard-metric-value">
                        {engagementMetrics?.totalCustomers || 0}
                      </p>
                    </div>
                    <div className="dashboard-metric-cell">
                      <p className="dashboard-metric-label">Active This Week</p>
                      <p className="dashboard-metric-value dashboard-metric-value--green">
                        {engagementMetrics?.activeThisWeek || 0}
                        <span className="dashboard-metric-pct">
                          {' '}({(engagementMetrics?.weeklyActiveRate ?? 0).toFixed(0)}%)
                        </span>
                      </p>
                    </div>
                    <div className="dashboard-metric-cell">
                      <p className="dashboard-metric-label">Discord Linked</p>
                      <p className="dashboard-metric-value">
                        {engagementMetrics?.discordLinked || 0}
                        <span className="dashboard-metric-pct">
                          {' '}({(engagementMetrics?.discordLinkRate ?? 0).toFixed(0)}%)
                        </span>
                      </p>
                    </div>
                    <div className="dashboard-metric-cell">
                      <p className="dashboard-metric-label">Completed Books</p>
                      <p className="dashboard-metric-value">
                        {engagementMetrics?.completedBooks || 0}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Content Performance */}
                <Card title="Content Performance" subtitle="Book and material analytics">
                  <div className="dashboard-content-perf-grid">
                    <div>
                      <h4 className="dashboard-content-perf-heading">Books</h4>
                      {contentMetrics?.books?.map((book: any) => (
                        <div key={book.id} className="dashboard-content-perf-row">
                          <span className="dashboard-content-perf-name">{book.title}</span>
                          <div className="dashboard-content-perf-stats">
                            <span>{book.uniqueReaders} readers</span>
                            <span>{book.totalReads} reads</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 className="dashboard-content-perf-heading">Top Chapters</h4>
                      {contentMetrics?.topChapters?.slice(0, 5).map((chapter: any) => (
                        <div key={chapter.id} className="dashboard-content-perf-row">
                          <span className="dashboard-content-perf-name">Ch. {chapter.number}: {chapter.title}</span>
                          <span className="dashboard-content-perf-single-stat">{chapter.reads} reads</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {activeTab === 'agents' && showAsAdmin && (
              <motion.div
                key="agents"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="mb-6">
                  <h1 className="dashboard-page-title">
                    Agent Management
                  </h1>
                  <p className="dashboard-page-subtitle">
                    Monitor and control system agents, review pending insights
                  </p>
                </div>

                {/* Agent Status Table */}
                <Card title="Agents" subtitle="System agent status and controls">
                  {agentsList && agentsList.length > 0 ? (
                    <div className="dashboard-table-wrap">
                      <table className="dashboard-table">
                        <thead>
                          <tr>
                            <th>Agent</th>
                            <th>Status</th>
                            <th>Last Run</th>
                            <th>Success Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentsList.map((agent: any) => (
                            <tr key={agent.id}>
                              <td>
                                <div>
                                  <strong>{agent.name}</strong>
                                  {agent.description && (
                                    <p style={{ fontSize: '12px', color: '#999', margin: '2px 0 0' }}>{agent.description}</p>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className={`dashboard-badge-status ${
                                  agent.status === 'active' ? 'dashboard-badge-status--active' :
                                  agent.status === 'dormant' ? 'dashboard-badge-status--warning' :
                                  'dashboard-badge-status--inactive'
                                }`}>
                                  {agent.status}
                                </span>
                              </td>
                              <td style={{ fontSize: '13px', color: '#666' }}>
                                {agent.lastRunAt ? new Date(agent.lastRunAt).toLocaleString() : 'Never'}
                              </td>
                              <td style={{ fontSize: '13px' }}>
                                {agent.successRate != null ? `${(agent.successRate * 100).toFixed(0)}%` : '--'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="dashboard-empty-sm">
                      <Activity size={32} color="#ccc" className="mb-3" />
                      <p className="dashboard-empty-text-sm">No agents configured yet.</p>
                    </div>
                  )}
                </Card>

                {/* Pending Insights */}
                <Card title="Pending Insights" subtitle="Agent-generated content awaiting approval">
                  {pendingInsights && pendingInsights.length > 0 ? (
                    <div className="flex-col gap-3">
                      {pendingInsights.map((insight: any) => (
                        <div key={insight.id} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px',
                          background: '#fafafa',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div>
                              <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999' }}>
                                {insight.agentName} &middot; {insight.type}
                              </span>
                              <h4 style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 500 }}>
                                {insight.title}
                              </h4>
                            </div>
                            <span style={{ fontSize: '12px', color: '#999' }}>
                              {new Date(insight.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.5, margin: '0 0 12px' }}>
                            {insight.content?.length > 200 ? insight.content.slice(0, 200) + '...' : insight.content}
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={async () => {
                                await apiClient.admin.approveInsight(insight.id).catch(() => {});
                                setPendingInsights((prev) => prev?.filter((i: any) => i.id !== insight.id) ?? null);
                              }}
                              style={{
                                background: '#16a34a',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                padding: '6px 14px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <Check size={14} />
                              Approve
                            </button>
                            <button
                              onClick={async () => {
                                await apiClient.admin.rejectInsight(insight.id).catch(() => {});
                                setPendingInsights((prev) => prev?.filter((i: any) => i.id !== insight.id) ?? null);
                              }}
                              style={{
                                background: 'transparent',
                                border: '1px solid #ddd',
                                borderRadius: '5px',
                                padding: '6px 14px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                color: '#666',
                              }}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="dashboard-empty-sm">
                      <CheckCircle size={32} color="#ccc" className="mb-3" />
                      <p className="dashboard-empty-text-sm">No pending insights. All caught up.</p>
                    </div>
                  )}
                </Card>

                {/* Recent Agent Activity */}
                <Card title="Recent Activity" subtitle="Latest agent actions and events">
                  {agentLog && agentLog.length > 0 ? (
                    <div className="flex-col gap-2">
                      {agentLog.map((entry: any) => (
                        <div key={entry.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: entry.status === 'success' ? '#16a34a' : entry.status === 'error' ? '#dc2626' : '#f59e0b',
                            flexShrink: 0,
                          }} />
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '14px', margin: 0 }}>
                              <strong>{entry.agentName}</strong>: {entry.action}
                            </p>
                            {entry.details && (
                              <p style={{ fontSize: '12px', color: '#999', margin: '2px 0 0' }}>{entry.details}</p>
                            )}
                          </div>
                          <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap' }}>
                            {new Date(entry.createdAt).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="dashboard-empty-sm">
                      <Activity size={32} color="#ccc" className="mb-3" />
                      <p className="dashboard-empty-text-sm">No agent activity recorded yet.</p>
                    </div>
                  )}
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showAddBook && showAsAdmin && (
          <Modal onClose={() => { setShowAddBook(false); setManuscriptFile(null); setCoverFile(null); setParsedChapters(null); }} title="Upload New Book">
            <div className="flex-col gap-4">
              {/* Manuscript Upload */}
              <FileUploadField
                label="Upload Manuscript"
                accept=".pdf,.docx,.doc,.txt"
                hint="PDF, Word, or text file. Chapters will be auto-detected."
                file={manuscriptFile}
                onFileChange={setManuscriptFile}
                icon={<File size={20} />}
              />

              {/* Cover Upload */}
              <FileUploadField
                label="Upload Cover Image"
                accept="image/*"
                hint="JPG, PNG, or WebP. Recommended: 400x600px"
                file={coverFile}
                onFileChange={setCoverFile}
                icon={<Image size={20} />}
              />

              {/* Optional overrides */}
              <InputField label="Book Title (optional - auto-detected from manuscript)" placeholder="Leave blank to auto-detect" value={newBookTitle} onChange={setNewBookTitle} />
              <InputField label="Subtitle" placeholder="e.g., A Journey to Better Health" value={newBookSubtitle} onChange={setNewBookSubtitle} />
              <InputField label="URL Slug (optional)" placeholder="auto-generated-from-title" value={newBookSlug} onChange={setNewBookSlug} hint={newBookSlug ? `Reader URL: /reader/${newBookSlug}` : 'Will be auto-generated from title'} />

              {/* Progress indicator */}
              {uploadProgress && (
                <div className="dashboard-upload-progress">
                  <Loader2 size={16} className="dashboard-loading-spinner" />
                  {uploadProgress}
                </div>
              )}

              {/* Parsed chapters preview */}
              {parsedChapters && (
                <div className="dashboard-parsed-preview">
                  Detected {parsedChapters.length} chapters
                </div>
              )}

              <div className="dashboard-modal-actions">
                <button onClick={() => { setShowAddBook(false); setManuscriptFile(null); setCoverFile(null); setParsedChapters(null); }} className="dashboard-modal-btn-cancel">
                  Cancel
                </button>
                <button onClick={handleAddBook} disabled={(!manuscriptFile && !newBookTitle) || isSubmitting} className={`dashboard-modal-btn-submit ${((!manuscriptFile && !newBookTitle) || isSubmitting) ? 'dashboard-modal-btn-submit--disabled' : ''}`}>
                  {isSubmitting ? 'Processing...' : 'Create Book'}
                </button>
              </div>
            </div>
          </Modal>
        )}

        {showAddMaterial && showAsAdmin && (
          <Modal onClose={() => { setShowAddMaterial(false); setMaterialFile(null); setSelectedBookForMaterial(null); }} title="Upload Material">
            <div className="flex-col gap-4">
              {/* File Upload */}
              <FileUploadField
                label="Upload File"
                accept=".pdf,.mp4,.mov,.webp,.doc,.docx"
                hint="PDF, video, or document"
                file={materialFile}
                onFileChange={setMaterialFile}
                icon={<FileText size={20} />}
              />

              <InputField label="Material Name" placeholder="e.g., Quick Start Guide" value={newMaterialName} onChange={setNewMaterialName} />
              <InputField label="Description" placeholder="Brief description" value={newMaterialDesc} onChange={setNewMaterialDesc} />

              {/* Link to book */}
              <div>
                <label className="dashboard-field-label">Link to Book (optional)</label>
                <select
                  value={selectedBookForMaterial || ''}
                  onChange={(e) => setSelectedBookForMaterial(e.target.value || null)}
                  className="dashboard-select"
                >
                  <option value="">No book (global material)</option>
                  {books?.map(book => (
                    <option key={book.id} value={book.id}>{book.title}</option>
                  ))}
                </select>
                <p className="dashboard-field-hint">Associate this material with a specific book</p>
              </div>

              {/* Progress indicator */}
              {uploadProgress && (
                <div className="dashboard-upload-progress">
                  <Loader2 size={16} className="dashboard-loading-spinner" />
                  {uploadProgress}
                </div>
              )}

              <div className="dashboard-modal-actions">
                <button onClick={() => { setShowAddMaterial(false); setMaterialFile(null); setSelectedBookForMaterial(null); }} className="dashboard-modal-btn-cancel">
                  Cancel
                </button>
                <button onClick={handleAddMaterial} disabled={!newMaterialName || (!materialFile && !newMaterialUrl) || isSubmitting} className={`dashboard-modal-btn-submit dashboard-modal-btn-submit--amber ${(!newMaterialName || (!materialFile && !newMaterialUrl) || isSubmitting) ? 'dashboard-modal-btn-submit--disabled' : ''}`}>
                  {isSubmitting ? 'Uploading...' : 'Add Material'}
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Add Chapter Modal */}
        {showAddChapter && showAsAdmin && selectedBook && (
          <Modal onClose={() => { setShowAddChapter(false); setNewChapterTitle(''); setNewChapterContent(''); }} title="Add New Chapter">
            <div className="flex-col gap-4">
              <InputField
                label="Chapter Title"
                placeholder="e.g., Introduction"
                value={newChapterTitle}
                onChange={setNewChapterTitle}
              />
              <div>
                <label className="dashboard-field-label">Chapter Content</label>
                <textarea
                  value={newChapterContent}
                  onChange={(e) => setNewChapterContent(e.target.value)}
                  placeholder="Enter chapter content here..."
                  className="dashboard-textarea"
                />
                <p className="dashboard-field-hint">
                  You can paste content or type directly
                </p>
              </div>
              <div className="dashboard-modal-actions">
                <button
                  onClick={() => { setShowAddChapter(false); setNewChapterTitle(''); setNewChapterContent(''); }}
                  className="dashboard-modal-btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddChapter}
                  disabled={!newChapterTitle.trim() || isSubmitting}
                  className={`dashboard-modal-btn-submit ${(!newChapterTitle.trim() || isSubmitting) ? 'dashboard-modal-btn-submit--disabled' : ''}`}
                >
                  {isSubmitting ? 'Adding...' : 'Add Chapter'}
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Edit Chapter Modal */}
        {editingChapter && showAsAdmin && (
          <Modal onClose={() => setEditingChapter(null)} title="Edit Chapter">
            <div className="flex-col gap-4">
              <InputField
                label="Chapter Title"
                placeholder="Chapter title"
                value={editingChapter.title}
                onChange={(v) => setEditingChapter({ ...editingChapter, title: v })}
              />
              <div>
                <label className="dashboard-field-label">Chapter Content</label>
                <textarea
                  value={editingChapter.content}
                  onChange={(e) => setEditingChapter({ ...editingChapter, content: e.target.value })}
                  placeholder="Chapter content..."
                  className="dashboard-textarea dashboard-textarea--tall"
                />
              </div>
              <div className="dashboard-checkbox-row">
                <input
                  type="checkbox"
                  id="freePreview"
                  checked={editingChapter.isFreePreview}
                  onChange={(e) => setEditingChapter({ ...editingChapter, isFreePreview: e.target.checked })}
                  className="dashboard-checkbox"
                />
                <label htmlFor="freePreview" className="dashboard-checkbox-label">
                  Free preview (visible without purchase)
                </label>
              </div>
              <div className="dashboard-modal-actions">
                <button
                  onClick={() => setEditingChapter(null)}
                  className="dashboard-modal-btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateChapter}
                  disabled={!editingChapter.title.trim() || isSubmitting}
                  className={`dashboard-modal-btn-submit dashboard-modal-btn-submit--accent ${(!editingChapter.title.trim() || isSubmitting) ? 'dashboard-modal-btn-submit--disabled' : ''}`}
                >
                  <Save size={16} />
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Material Viewer Modal */}
      {viewingMaterial && (
        <MaterialViewer
          material={viewingMaterial}
          onClose={() => setViewingMaterial(null)}
        />
      )}
    </div>
  );
}

// Components
function NavItem({ icon, label, active, onClick, badge }: { icon: React.ReactNode; label: string; active?: boolean; onClick: () => void; badge?: number }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`dashboard-nav-item ${active ? 'dashboard-nav-item--active' : ''}`}
    >
      {icon}
      <span className="dashboard-nav-item-label">
        {label}
        <span
          className="dashboard-nav-item-underline"
          style={{ width: hovered || active ? '100%' : '0%' }}
        />
      </span>
      {badge !== undefined && (
        <span className={`dashboard-nav-item-badge ${active ? 'dashboard-nav-item-badge--active' : ''}`}>
          {badge}
        </span>
      )}
    </button>
  );
}

function StatCard({ icon, label, value, change }: { icon: React.ReactNode; label: string; value: string; change?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="dashboard-stat-card"
    >
      <div className="dashboard-stat-header">
        {icon}
        <span className="dashboard-stat-label">{label}</span>
      </div>
      <div className="dashboard-stat-body">
        <span className="dashboard-stat-value">{value}</span>
        {change !== undefined && (
          <span className={`dashboard-stat-change ${change >= 0 ? 'dashboard-stat-change--positive' : 'dashboard-stat-change--negative'}`}>
            <TrendingUp size={12} /> {change >= 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
    </motion.div>
  );
}

function Card({ children, title, subtitle, action }: { children: React.ReactNode; title?: string; subtitle?: string; action?: { label: string; onClick: () => void; icon?: React.ReactNode } }) {
  return (
    <div className="dashboard-card-wrap">
      {(title || action) && (
        <div className="dashboard-card-header-row">
          <div>
            {title && <h3 className="dashboard-card-title-text">{title}</h3>}
            {subtitle && <p className="dashboard-card-subtitle-text">{subtitle}</p>}
          </div>
          {action && (
            <button onClick={action.onClick} className="dashboard-card-action-btn">
              {action.icon}
              {action.label}
              {!action.icon && <ChevronRight size={14} />}
            </button>
          )}
        </div>
      )}
      <div className="dashboard-card-body">{children}</div>
    </div>
  );
}

function BookCard({ book, index, isAdmin, isSelected, onSelect, onDelete, onToggleVisibility, onUploadCover }: { book: any; index: number; isAdmin: boolean; isSelected: boolean; onSelect: () => void; onDelete: () => void; onToggleVisibility?: () => void; onUploadCover?: (file: File) => void }) {
  const coverInputId = `cover-upload-${book.id}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onSelect}
      className={`dashboard-book-card ${isSelected ? 'dashboard-book-card--selected' : ''}`}
    >
      {/* Cover Image Area - Full book cover display */}
      <div className="dashboard-book-cover-area">
        {book.coverUrl ? (
          <img
            src={book.coverUrl}
            alt={book.title}
            className="dashboard-book-cover-img"
          />
        ) : (
          <BookOpen size={48} color="rgba(255,255,255,0.2)" />
        )}

        {/* Status badge */}
        <span className={`dashboard-book-status ${book.status === 'published' ? 'dashboard-book-status--published' : 'dashboard-book-status--draft'}`}>
          {book.status}
        </span>

        {/* Upload cover button (admin only) */}
        {isAdmin && onUploadCover && (
          <label
            htmlFor={coverInputId}
            onClick={(e) => e.stopPropagation()}
            className="dashboard-book-cover-upload"
          >
            <Image size={12} /> {book.coverUrl ? 'Change' : 'Add Cover'}
            <input
              id={coverInputId}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUploadCover(file);
              }}
              className="hidden"
            />
          </label>
        )}
      </div>

      <div className="dashboard-book-info">
        <h3 className="dashboard-book-card-title">{book.title}</h3>
        <p className="dashboard-book-card-subtitle">{book.subtitle || 'No subtitle'}</p>
        <div className="dashboard-book-meta">
          <span>{book.chapters.length} chapters</span>
          {isAdmin && <span>{(book.reader_count || 0).toLocaleString()} readers</span>}
          {isAdmin && <span className="dashboard-book-rating"><Star size={12} fill="#f59e0b" color="#f59e0b" /> {book.avgRating || 'N/A'}</span>}
        </div>

        {/* Customer view: Big "Start Reading" button */}
        {!isAdmin && (
          <Link
            to={`/reader/${book.slug}`}
            onClick={(e) => e.stopPropagation()}
            className="dashboard-book-read-btn"
          >
            <BookOpen size={18} /> Start Reading
          </Link>
        )}

        {/* Admin view: Management buttons */}
        {isAdmin && (
          <div className="dashboard-book-admin-actions">
            <Link to={`/reader/${book.slug}`} onClick={(e) => e.stopPropagation()} className="dashboard-book-preview-btn">
              <Eye size={14} /> Preview
            </Link>
            {onToggleVisibility && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}
                className={`dashboard-book-visibility-btn ${book.status === 'published' ? 'dashboard-book-visibility-btn--published' : 'dashboard-book-visibility-btn--draft'}`}
                title={book.status === 'published' ? 'Click to unpublish' : 'Click to publish'}
              >
                {book.status === 'published' ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="dashboard-book-delete-btn">
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function ChapterRow({ chapter, isAdmin, onEdit, onDelete }: { chapter: any; isAdmin: boolean; onEdit?: () => void; onDelete?: () => void }) {
  return (
    <div className="dashboard-chapter-row">
      <span className="dashboard-chapter-row-number">
        {chapter.number}
      </span>
      <div className="flex-1">
        <div className="dashboard-chapter-row-header">
          <h4 className="dashboard-chapter-row-title">{chapter.title}</h4>
          {chapter.isFreePreview && (
            <span className="dashboard-badge-free">
              Free
            </span>
          )}
        </div>
        <p className="dashboard-chapter-row-words">{chapter.wordCount?.toLocaleString() || 0} words</p>
      </div>
      <span className={`dashboard-chapter-row-status ${chapter.status === 'published' ? 'dashboard-chapter-row-status--published' : 'dashboard-chapter-row-status--draft'}`}>
        {chapter.status}
      </span>
      {isAdmin && (
        <div className="dashboard-chapter-row-actions">
          <button
            onClick={onEdit}
            className="dashboard-chapter-edit-btn"
            title="Edit chapter"
          >
            <Edit3 size={14} />
          </button>
          <button
            onClick={onDelete}
            className="dashboard-chapter-delete-btn"
            title="Delete chapter"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function MaterialCard({ material, index, isAdmin, onDelete, onView }: { material: any; index: number; isAdmin: boolean; onDelete: () => void; onView: () => void }) {
  const typeColors: Record<string, string> = { pdf: '#ef4444', video: '#8b5cf6', guide: '#3b82f6', template: '#10b981' };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onView}
      className="dashboard-material-card"
      whileHover={{
        borderColor: 'var(--accent, #dc2626)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      }}
    >
      <div className="dashboard-material-card-top">
        <div className="dashboard-material-icon" style={{ background: `${typeColors[material.type] || '#888'}15`, color: typeColors[material.type] || '#888' }}>
          <FileText size={20} />
        </div>
        <div className="dashboard-material-info">
          <h4 className="dashboard-material-name">{material.name}</h4>
          <p className="dashboard-material-desc">{material.description || 'No description'}</p>
        </div>
      </div>
      <div className="dashboard-material-card-bottom">
        <div className="dashboard-material-stats">
          <span className="dashboard-material-stat-item"><Download size={12} /> {material.downloads?.toLocaleString() || 0}</span>
          <span>{material.size || 'N/A'}</span>
        </div>
        <div className="dashboard-material-actions">
          <button
            onClick={(e) => { e.stopPropagation(); onView(); }}
            className="dashboard-material-view-btn"
          >
            <Eye size={14} /> View
          </button>
          {isAdmin && (
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="dashboard-material-delete-btn"><Trash2 size={14} /></button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="dashboard-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="dashboard-modal-box"
      >
        <h2 className="dashboard-modal-title-text">{title}</h2>
        {children}
      </motion.div>
    </motion.div>
  );
}

function InputField({ label, placeholder, value, onChange, hint }: { label: string; placeholder: string; value: string; onChange: (v: string) => void; hint?: string }) {
  return (
    <div>
      <label className="dashboard-field-label">{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="dashboard-input-field"
      />
      {hint && <p className="dashboard-field-hint">{hint}</p>}
    </div>
  );
}

function FileUploadField({ label, accept, hint, file, onFileChange, icon }: { label: string; accept: string; hint: string; file: File | null; onFileChange: (file: File | null) => void; icon: React.ReactNode }) {
  const inputId = `file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div>
      <label className="dashboard-field-label">{label}</label>
      <label
        htmlFor={inputId}
        className={`dashboard-file-drop ${file ? 'dashboard-file-drop--active' : ''}`}
      >
        <input
          id={inputId}
          type="file"
          accept={accept}
          onChange={(e) => onFileChange(e.target.files?.[0] || null)}
          className="hidden"
        />
        <div className={`dashboard-file-icon ${file ? 'dashboard-file-icon--active' : ''}`}>{icon}</div>
        <div className="text-left">
          {file ? (
            <>
              <p className="dashboard-file-name">{file.name}</p>
              <p className="dashboard-file-size">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </>
          ) : (
            <>
              <p className="dashboard-file-prompt">Click to upload</p>
              <p className="dashboard-file-hint">{hint}</p>
            </>
          )}
        </div>
        {file && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFileChange(null); }}
            className="dashboard-file-remove"
          >
            <Trash2 size={14} />
          </button>
        )}
      </label>
    </div>
  );
}

function BroadcastCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<{ sent: number; failed: number } | null>(null);

  const handleSend = async () => {
    if (!message.trim()) return;

    setIsSending(true);
    setResult(null);

    try {
      const res = await apiClient.admin.broadcast('discord', message) as { sent: number; failed: number };
      setResult(res);
      setMessage('');
    } catch (err) {
      // Broadcast failed
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="dashboard-broadcast-card">
      <div className="dashboard-broadcast-header">
        <div className="dashboard-broadcast-icon">
          {icon}
        </div>
        <div>
          <h3 className="dashboard-broadcast-title">{title}</h3>
          <p className="dashboard-broadcast-desc">{description}</p>
        </div>
      </div>

      <textarea
        placeholder="Type your Discord message..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        className="dashboard-textarea"
      />

      {result && (
        <div className={`dashboard-broadcast-result ${result.failed > 0 ? 'dashboard-broadcast-result--warning' : 'dashboard-broadcast-result--success'}`}>
          Sent to {result.sent} customers{result.failed > 0 && `, ${result.failed} failed`}
        </div>
      )}

      <button
        onClick={handleSend}
        disabled={isSending || !message.trim()}
        className={`dashboard-broadcast-send ${(isSending || !message.trim()) ? 'dashboard-broadcast-send--disabled' : ''}`}
      >
        {isSending ? (
          <>
            <Loader2 size={16} className="dashboard-loading-spinner" />
            Sending...
          </>
        ) : (
          <>
            <Send size={16} />
            Send Broadcast
          </>
        )}
      </button>
    </div>
  );
}

function AffiliateRow({ affiliate }: { affiliate: any }) {
  const [copied, setCopied] = useState(false);

  const referralLink = `https://fastingcarnivore.com/ref/${affiliate.code}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="dashboard-affiliate-row">
      <div className={`dashboard-member-avatar ${affiliate.status !== 'active' ? 'dashboard-member-avatar--inactive' : ''}`}>
        {(affiliate.user?.name || affiliate.user?.email || 'A').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1">
        <h4 className="dashboard-member-name">
          {affiliate.user?.name || 'No name'}
        </h4>
        <p className="dashboard-member-email">
          {affiliate.user?.email} · Code: <strong>{affiliate.code}</strong>
        </p>
      </div>
      <div className="flex gap-2">
        <span className={`dashboard-badge-status ${affiliate.status === 'active' ? 'dashboard-badge-status--active' : 'dashboard-badge-status--inactive'}`}>
          {affiliate.status}
        </span>
        <span className="dashboard-badge-commission">
          {(affiliate.commissionRate * 100).toFixed(0)}%
        </span>
      </div>
      <div className="dashboard-affiliate-earnings">
        <p className="dashboard-affiliate-total">
          ${affiliate.totalEarnings.toFixed(2)}
        </p>
        <p className="dashboard-affiliate-referrals">
          {affiliate.totalReferrals} referrals
        </p>
      </div>
      <button
        onClick={handleCopy}
        className={`dashboard-affiliate-copy ${copied ? 'dashboard-affiliate-copy--copied' : ''}`}
        title="Copy referral link"
      >
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
}
